/**
 * React Hook for Workflow Search
 * 
 * Provides search functionality across multiple workflow types
 */

import { useCallback, useEffect, useState } from 'react';
import { complexProcessRepository } from '../repositories/ComplexProcessRepository';
import { ComplexProcessDefinition } from '../types/ComplexProcess';

export interface SearchResult {
    id: string;
    type: string;
    title: string;
    description: string;
    status: string;
    created: string;
    modified: string;
    tags: string[];
    score: number;
}

export interface SearchFilters {
    type?: string;
    status?: string;
    sortBy?: string;
    limit?: number;
    refresh?: boolean;
}

export function useWorkflowSearch(initialQuery: string = '', debounceMs: number = 300) {
    const [results, setResults] = useState<SearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [currentQuery, setCurrentQuery] = useState(initialQuery);
    const [currentFilters, setCurrentFilters] = useState<SearchFilters>({});

    const searchWorkflow = useCallback(async (query: string, filters: SearchFilters = {}) => {
        setCurrentQuery(query);
        setCurrentFilters(filters);

        setLoading(true);
        setError(null);

        try {
            if (!query || query.trim() === '') {
                // Load all processes for this type
                const processes = await complexProcessRepository.findAll();

                // Filter by type if specified
                let filteredProcesses = processes;
                if (filters.type) {
                    filteredProcesses = processes.filter(p =>
                        p.domain.toLowerCase().includes(filters.type!.toLowerCase())
                    );
                }

                // Transform to search results format
                const searchResults: SearchResult[] = filteredProcesses.map(process => ({
                    id: process.id,
                    type: 'ComplexProcess',
                    title: process.name,
                    description: process.description,
                    status: process.active ? 'active' : 'inactive',
                    created: String(process.createdAt || ''),
                    modified: String(process.updatedAt || ''),
                    tags: [process.domain, process.complexity, ...(process.isTemplate ? ['template'] : [])],
                    score: 100
                }));

                // Apply limit if specified
                const limitedResults = filters.limit ? searchResults.slice(0, filters.limit) : searchResults;
                setResults(limitedResults);
            } else {
                // Search through complex processes using our repository
                const processes = await complexProcessRepository.search(query);

                // Transform to search results format
                const searchResults: SearchResult[] = processes.map(process => ({
                    id: process.id,
                    type: 'ComplexProcess',
                    title: process.name,
                    description: process.description,
                    status: process.active ? 'active' : 'inactive',
                    created: String(process.createdAt || ''),
                    modified: String(process.updatedAt || ''),
                    tags: [process.domain, process.complexity, ...(process.isTemplate ? ['template'] : [])],
                    score: calculateRelevanceScore(process, query)
                }));

                setResults(searchResults);
            }
        } catch (err) {
            setError(err as Error);
            setResults([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Auto-search when initialQuery changes (for backward compatibility)
    useEffect(() => {
        if (initialQuery) {
            const timer = setTimeout(() => {
                searchWorkflow(initialQuery, currentFilters);
            }, debounceMs);

            return () => clearTimeout(timer);
        }
        return undefined;
    }, [initialQuery, searchWorkflow, currentFilters, debounceMs]);

    return {
        searchResults: results,
        results, // Alias for backward compatibility
        loading,
        error,
        searchWorkflow
    };
}

function calculateRelevanceScore(process: ComplexProcessDefinition, query: string): number {
    const lowerQuery = query.toLowerCase();
    let score = 0;

    // Name match gets highest score
    if (process.name.toLowerCase().includes(lowerQuery)) {
        score += 50;
    }

    // Description match
    if (process.description.toLowerCase().includes(lowerQuery)) {
        score += 30;
    }

    // Domain match
    if (process.domain.toLowerCase().includes(lowerQuery)) {
        score += 20;
    }

    return Math.min(score, 100);
}

export default useWorkflowSearch;